import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, CircularProgress, Alert, Button, Card, CardContent,
    Grid, TextField, InputAdornment, ToggleButtonGroup, ToggleButton, Avatar, Chip, Stack
} from '@mui/material';
import { 
    Search as SearchIcon, 
    ViewModule as ViewModuleIcon, 
    ViewList as ViewListIcon, 
    AccessTime as AccessTimeIcon,
    Refresh as RefreshIcon,
    Person as PersonIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

function Inbox() {
    const navigate = useNavigate();
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [search, setSearch] = useState('');
    
    // Auth State
    const [authRequired, setAuthRequired] = useState(false);
    const [authData, setAuthData] = useState(null);
    const [polling, setPolling] = useState(false);

    useEffect(() => {
        fetchEmails();
    }, []);

    const fetchEmails = async () => {
        setLoading(true);
        setAuthRequired(false);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config}/api/inbox`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEmails(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching emails:', err);
            if (err.response && err.response.status === 403 && err.response.data.error === 'AuthRequired') {
                setAuthRequired(true);
                setError(null);
            } else {
                const errorMsg = err.response?.data?.error || 'Failed to load emails.';
                setError(errorMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const startLogin = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config}/api/inbox/login`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAuthData(response.data);
            pollForToken(response.data.flow);
        } catch (err) {
            setError('Failed to initiate login flow.');
        } finally {
            setLoading(false);
        }
    };

    const pollForToken = async (flow) => {
        setPolling(true);
        try {
            const token = localStorage.getItem('token');
            // This request will hang until user approves or it times out
            await axios.post(`${config}/api/inbox/poll`, { flow }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 120000 // 2 minutes timeout
            });
            // If success
            setAuthData(null);
            setPolling(false);
            fetchEmails();
        } catch (err) {
            console.error("Polling error", err);
            setPolling(false);
            if (err.code === 'ECONNABORTED') {
                 setError('Login timed out. Please try again.');
            } else {
                 setError(err.response?.data?.error || 'Login failed.');
            }
        }
    };

    // Filter emails
    const filteredEmails = emails.filter(e => 
        e.subject.toLowerCase().includes(search.toLowerCase()) || 
        e.sender.toLowerCase().includes(search.toLowerCase()) ||
        (e.preview && e.preview.toLowerCase().includes(search.toLowerCase()))
    );

    if (authRequired) {
        return (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="h4" gutterBottom>Inbox Access Required</Typography>
                
                {!authData ? (
                    <Button variant="contained" onClick={startLogin} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Connect Office 365 Account'}
                    </Button>
                ) : (
                    <Card sx={{ maxWidth: 400, textAlign: 'center', mt: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Scan Code or Visit URL
                            </Typography>
                            {authData.qr_code && (
                                <Box sx={{ mb: 2 }}>
                                    <img 
                                        src={`data:image/png;base64,${authData.qr_code}`} 
                                        alt="Scan QR Code" 
                                        style={{ width: '200px', height: '200px' }} 
                                    />
                                </Box>
                            )}
                            <Typography variant="body1">
                                1. Visit: <a href={authData.verification_uri} target="_blank" rel="noopener noreferrer">{authData.verification_uri}</a>
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.2rem', my: 1 }}>
                                2. Enter Code: {authData.user_code}
                            </Typography>
                            
                            {polling && (
                                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Waiting for approval...
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 3, gap: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                    Inbox
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField 
                        placeholder="Search emails..." 
                        size="small" 
                        value={search} 
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            sx: { borderRadius: 5, bgcolor: 'white' }
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                      size="small"
                      sx={{ bgcolor: 'white', borderRadius: 2 }}
                    >
                      <ToggleButton value="grid" aria-label="grid view">
                        <ViewModuleIcon />
                      </ToggleButton>
                      <ToggleButton value="list" aria-label="list view">
                        <ViewListIcon />
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Button 
                        variant="outlined" 
                        startIcon={<RefreshIcon />} 
                        onClick={fetchEmails}
                        sx={{ borderRadius: 5 }}
                    >
                        Refresh
                    </Button>
                    </Box>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                    <CircularProgress />
                </Box>
            ) : (
                viewMode === 'grid' ? (
                    <Grid container spacing={3}>
                        {filteredEmails.length === 0 ? (
                            <Grid item xs={12}>
                                <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>No emails found</Typography>
                            </Grid>
                        ) : filteredEmails.map((email) => (
                            <Grid item xs={12} md={6} lg={4} key={email.id}>
                                <Card 
                                    sx={{ height: '100%', borderRadius: 3, boxShadow: 1, cursor: 'pointer', transition: '0.3s', '&:hover': { boxShadow: 4 } }} 
                                    onClick={() => navigate(`/inbox/${email.id}`)}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                                                <EmailIcon />
                                            </Avatar>
                                            <Box sx={{ overflow: 'hidden' }}>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2, mb: 0.5 }}>
                                                    {email.subject}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="textSecondary" noWrap>
                                                        {email.sender}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" sx={{ 
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            mb: 2,
                                            height: '4.5em' // Fixed height for alignment
                                        }}>
                                            {email.preview || "No preview available"}
                                        </Typography>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid #eee' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Typography variant="caption" color="textSecondary">{email.date}</Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead sx={{ bgcolor: 'grey.50' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Sender</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Preview</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredEmails.map((email) => (
                                    <TableRow 
                                        key={email.id} 
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                                        onClick={() => navigate(`/inbox/${email.id}`)}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                {email.subject}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'secondary.main' }}>
                                                    {email.sender.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2">{email.sender}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">{email.date}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 300 }}>
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {email.preview}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredEmails.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                            <Typography variant="body2" color="textSecondary">No emails found matching your criteria.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            )}
        </Box>
    );
}

export default Inbox;
